import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { Box, Link, Typography } from "@mui/material";
import { RiArrowRightUpLine, RiFlagLine, RiHourglassLine, RiQuestionAnswerLine } from "@remixicon/react";
import { srOnly, tokens } from "../../utils/figmaTokens";

// The ticket page's shared parts (047 step 5; Admin · Page Head · Ticket 10829:93565).

// A card on the page: white, a hairline border (`strong`: the Resolution card's darker one), 12 px
// radius, 20 px padding. A `ref` reaches the box (React 19 passes it as a prop).
export const Panel = ({ strong = false, gap = "12px", children, sx, ...rest }) => (
  <Box
    sx={{
      bgcolor: tokens.surface.default,
      border: `1px solid ${strong ? tokens.border.default : tokens.border.subtle}`,
      borderRadius: "12px",
      p: "20px",
      display: "flex",
      flexDirection: "column",
      gap,
      minWidth: 0,
      ...sx,
    }}
    {...rest}
  >
    {children}
  </Box>
);

// The 1 px line between a card's parts.
export const Line = () => <Box aria-hidden sx={{ height: "1px", flexShrink: 0, bgcolor: tokens.divider }} />;

// A card's head: the title, an optional grey line under it, and what sits on the right (a chip).
export const PanelHead = ({ title, line, aside }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "12px" }}>
    <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: "2px" }}>
      <Typography variant="titleCard" component="h2" sx={{ color: tokens.text.primary }}>
        {title}
      </Typography>
      {line && (
        <Typography variant="bodySmall" sx={{ color: tokens.text.secondary }}>
          {line}
        </Typography>
      )}
    </Box>
    {aside}
  </Box>
);

const TILE_TONES = {
  warning: [tokens.warning.bg, tokens.warning.ink],
  brand: [tokens.brand.subtle, tokens.icon.brand],
};
const TILE_ICONS = { flag: RiFlagLine, hourglass: RiHourglassLine, question: RiQuestionAnswerLine };

// The kind's icon tile (D59): 44 px with a 24 px icon in the head, 40 px with a 22 px icon on the
// report card.
export const KindTile = ({ tone, icon, size = 44 }) => {
  const [bg, color] = TILE_TONES[tone] || TILE_TONES.brand;
  const Icon = TILE_ICONS[icon] || RiQuestionAnswerLine;
  return (
    <Box
      aria-hidden
      sx={{ width: size, height: size, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: "10px", bgcolor: bg, color }}
    >
      <Icon size={size === 44 ? 24 : 22} />
    </Box>
  );
};

// "Open student page ↗" (the Family card's link): brand ink, the arrow 4 px after the text. It
// opens in a new tab and tells a screen reader so (047 D80).
export const ArrowLink = ({ to, children }) => (
  <Link
    component={RouterLink}
    to={to}
    target="_blank"
    rel="noopener noreferrer"
    underline="hover"
    sx={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: "4px", typography: "bodySmallStrong", color: tokens.brand.text }}
  >
    {children}
    <NewTabNote />
    <RiArrowRightUpLine size={14} aria-hidden />
  </Link>
);

// The words only a screen reader gets after a link that opens in a new tab (D80).
export const NewTabNote = () => <Box component="span" sx={srOnly}> (opens in a new tab)</Box>;
